import { useState, useMemo, useCallback, useEffect, type FocusEvent } from "react";
import { useTranslation } from "react-i18next";
import { type Dayjs } from "dayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { type DateRange, type DateRangeValidationError } from "@mui/x-date-pickers-pro/models";
import { type PickersShortcutsItem, type PickersShortcutsProps } from "@mui/x-date-pickers/PickersShortcuts";
import { type PickerChangeHandlerContext } from "@mui/x-date-pickers/models";
import { usePickerContext } from "@mui/x-date-pickers/hooks";
import { type PickerRangeValue } from "@mui/x-date-pickers/internals";
import { PickersTextField } from "@mui/x-date-pickers/PickersTextField";
import { List, ListItemButton, ListItemText, styled, type SxProps } from "~/common/mui";

import CheckIcon from "@mui/icons-material/Check";
import { SHORTCUT_DEFINITIONS, getShortcutLabel, type ShortcutKey } from "./shortcuts";
import { DateFormatType, getPattern } from "~/common/utils/DateFormatter";

export interface AdvancedDateRangeValue {
  shortcut: ShortcutKey | null;
  dates: [Dayjs | null, Dayjs | null];
}

export interface AdvancedDateRangePickerProps {
  value?: AdvancedDateRangeValue;
  onChange?: (value: AdvancedDateRangeValue) => void;
  disabled?: boolean;
  label?: string;
  sx?: SxProps;
}

const OverlayWrapper = styled("div")({
  position: "relative",
  width: "100%",
});

const OverlayText = styled("span")(({ theme }) => ({
  position: "absolute",
  left: theme.spacing(1.75),
  top: "50%",
  transform: "translateY(-50%)",
  pointerEvents: "none",
  color: theme.palette.text.primary,
  fontSize: theme.typography.bodyLarge.fontSize,
  lineHeight: theme.typography.bodyLarge.lineHeight,
}));

const StyledShortcutsList = styled(List)(({ theme }) => ({
  padding: 0,
  minWidth: 160,
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));

type CustomTextFieldProps = React.ComponentPropsWithRef<typeof PickersTextField> & {
  showOverlay: boolean;
  overlayText: string;
};

const CustomTextField = ({ showOverlay, overlayText, sx, ...rest }: CustomTextFieldProps) => {
  return (
    <OverlayWrapper>
      <PickersTextField
        {...rest}
        sx={[
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
          showOverlay && {
            "& .MuiPickersSectionList-root": { color: "transparent" },
          },
        ]}
      />
      {showOverlay && <OverlayText>{overlayText}</OverlayText>}
    </OverlayWrapper>
  );
};

interface ShortcutsPanelProps extends PickersShortcutsProps<DateRange<Dayjs>> {
  selectedShortcut?: ShortcutKey | null;
}

const ShortcutsPanel = ({
  items = [],
  selectedShortcut,
  changeImportance = "accept",
  ...rest
}: ShortcutsPanelProps) => {
  const { setValue } = usePickerContext<PickerRangeValue>();

  const handleClick = (item: PickersShortcutsItem<DateRange<Dayjs>>) => {
    const newValue = item.getValue({ isValid: () => true });
    setValue(newValue as PickerRangeValue, {
      changeImportance,
      shortcut: { label: item.label, id: item.id },
    });
  };

  return (
    <StyledShortcutsList {...rest}>
      {items.map((item) => (
        <StyledListItemButton
          key={item.id ?? item.label}
          selected={selectedShortcut === item.id}
          onClick={() => handleClick(item)}
        >
          <CheckIcon fontSize="small" sx={{ mr: 1, visibility: selectedShortcut !== item.id ? "hidden" : undefined }} />
          <ListItemText primary={item.label} />
        </StyledListItemButton>
      ))}
    </StyledShortcutsList>
  );
};

const defaultValue: AdvancedDateRangeValue = { shortcut: null, dates: [null, null] };

export const AdvancedDateRangePicker = ({
  sx,
  value = defaultValue,
  onChange,
  disabled = false,
  label: _label,
}: AdvancedDateRangePickerProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [internalValue, setInternalValue] = useState<AdvancedDateRangeValue>(value);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleFocus = useCallback((_e: FocusEvent<HTMLDivElement>) => setFocused(true), []);
  const handleBlur = useCallback((_e: FocusEvent<HTMLDivElement>) => setFocused(false), []);

  const showOverlay = !focused && !open && value.shortcut !== "custom" && value.shortcut !== null;
  const overlayText = value.shortcut ? getShortcutLabel(value.shortcut, t) : "";

  const handleDateChange = (
    newDates: DateRange<Dayjs>,
    context: PickerChangeHandlerContext<DateRangeValidationError>,
  ) => {
    const isShortcut = !!context.shortcut;
    const shortcutKey = (context.shortcut?.id as ShortcutKey) ?? "custom";

    let resolvedDates: [Dayjs | null, Dayjs | null] = [newDates[0] ?? null, newDates[1] ?? null];

    if (!isShortcut) {
      const startChanged =
        (newDates[0] === null && internalValue.dates[0] !== null) ||
        (newDates[0] !== null && !newDates[0].isSame(internalValue.dates[0], "day"));

      if (startChanged) {
        resolvedDates = [newDates[0] ?? null, null];
      }
    }

    const nextValue: AdvancedDateRangeValue = {
      shortcut: shortcutKey,
      dates: resolvedDates,
    };

    setInternalValue(nextValue);

    if (isShortcut || (resolvedDates[0] && resolvedDates[1])) {
      onChange?.(nextValue);
    }
  };

  const shortcutItems: PickersShortcutsItem<DateRange<Dayjs>>[] = useMemo(
    () =>
      SHORTCUT_DEFINITIONS.map((shortcut) => ({
        label: t(shortcut.labelKey),
        getValue: () => shortcut.getValue(),
        id: shortcut.key,
      })),
    [t],
  );

  const shortcutsSlotProps: ShortcutsPanelProps = {
    items: shortcutItems,
    selectedShortcut: internalValue.shortcut,
  };

  return (
    <DateRangePicker
      sx={sx}
      value={internalValue.dates}
      onChange={handleDateChange}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      format={getPattern(DateFormatType.DATE)}
      disabled={disabled}
      calendars={2}
      displayWeekNumber
      slots={{
        shortcuts: ShortcutsPanel,
        textField: CustomTextField,
      }}
      localeText={{
        calendarWeekNumberText: (weekNumber) => t("common.calendar.calendarWeek", { weekNumber }),
        toolbarTitle: t("common.component.datePicker.title"),
      }}
      slotProps={{
        toolbar: {
          hidden: false,
          toolbarFormat: getPattern(DateFormatType.DATE),
        },
        popper: {
          placement: "bottom-end",
          sx: (theme) => ({
            "& .MuiPaper-root": {
              backgroundColor: theme.palette.surfaceContainer.low,
              boxShadow: theme.shadows[2],
            },
          }),
        },
        shortcuts: shortcutsSlotProps,
        textField: {
          showOverlay,
          overlayText,
          onFocus: handleFocus,
          onBlur: handleBlur,
        } as Partial<CustomTextFieldProps>,
      }}
    />
  );
};
